import { Menu, Pill, Tooltip, Typography } from '@recruit-robin/robin-theme/web-components';
import { Database_01, Edit_05, Indeed, Star_06 } from '@recruit-robin/robin-theme/web-icons';
import { memo, ReactNode } from 'react';

import type { JobImprovedType, JobListItemViewModel } from '../../models';

import { useDictionary } from '@/shared/lib/i18n';
import { useStyles } from './JobList.styles';

type JobListItemProps = {
  item: JobListItemViewModel;
  isSelected: boolean;
  onSelect: (item: JobListItemViewModel) => void;
  onEditJob?: (jobId: string) => void;
};

const qualityPillType = (qualityPercent: number | null | undefined): 'error' | 'warning' | 'success-light' => {
  if (!qualityPercent || qualityPercent < 33) return 'error';
  if (qualityPercent < 66) return 'warning';
  return 'success-light';
};

const getTypeIcon = (type: JobImprovedType): ReactNode => {
  switch (type) {
    case 'INDEED':
      return <Indeed size={16} />;
    case 'ATS_RAW':
      return <Database_01 size={16} />;
    default:
      return <Star_06 size={16} />;
  }
};

export const JobListItem = memo(({ item, isSelected, onSelect, onEditJob }: JobListItemProps) => {
  const classes = useStyles();
  const dictionary = useDictionary();

  const qualityLabel =
    item.quality == null || item.quality <= 33
      ? dictionary.lowQuality
      : item.quality <= 66
        ? dictionary.mediumQuality
        : dictionary.highQuality;

  const editTooltipLabel = item.isImproved ? dictionary.editVacancy : dictionary.enrichVacancy;

  const qualityPill = (
    <Pill
      label={qualityLabel}
      style="icon-removable"
      type={qualityPillType(item.quality)}
      size="small"
      padding="text"
    />
  );

  const editControl = onEditJob ? (
    <Tooltip label={editTooltipLabel} zIndex={100} placement="bottom-end" offset={10}>
      <div
        css={classes.editHitTarget}
        onClick={(event) => {
          event.stopPropagation();
          onEditJob(item.id);
        }}>
        <Edit_05 size={16} color={(c) => c.neutral.variant[60]} />
      </div>
    </Tooltip>
  ) : null;

  return (
    <Menu.Item.Root
      customCss={onEditJob ? [classes.menuItem, classes.menuItemWithEdit] : classes.menuItem}
      onClick={() => onSelect(item)}
      selected={isSelected}
      variant="subtle">
      <div css={classes.iconContainer}>{getTypeIcon(item.type)}</div>

      <Typography variant={(v) => v.supporting.helper} ellipsis>
        {item.name}
      </Typography>

      {onEditJob ? (
        <div css={classes.trailingSlot}>
          {item.quality != null && (
            <div css={classes.trailingQuality} data-job-list-trailing="quality">
              {qualityPill}
            </div>
          )}
          <div data-job-list-trailing="edit">{editControl}</div>
        </div>
      ) : (
        item.quality != null && <div css={classes.qualityPill}>{qualityPill}</div>
      )}
    </Menu.Item.Root>
  );
});

JobListItem.displayName = 'JobListItem';
